import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuthState } from "@/stores/auth-hooks";
import { FIELD_INFO_ENTITY_TYPES } from "./entity-types";
import { fieldInfoQueryOptions } from "./field-info.options";
import type { FieldInfoData } from "./field-info.types";

/**
 * Builds a flat map of entityType.propertyName -> label from all field-info list queries.
 * Used internally by useAppTranslation; components use t(key, { useFieldInfo: true }) instead.
 */
export function useFieldInfoLabels(): {
	getFieldLabel: (key: string) => string;
	isLoading: boolean;
} {
	const isAuthenticated = useAuthState((s) => s.isAuthenticated);

	const results = useQueries({
		queries: FIELD_INFO_ENTITY_TYPES.map((entityType) => {
			const base = fieldInfoQueryOptions.list(entityType);
			return {
				...base,
				enabled: Boolean(base.enabled) && isAuthenticated,
			};
		}),
	});

	const map = useMemo(() => {
		const out: Record<string, string> = {};
		for (const result of results) {
			const data = result.data as FieldInfoData[] | undefined;
			if (!Array.isArray(data)) continue;
			for (const item of data) {
				const key = `${item.entityType}.${item.propertyName}`;
				out[key] = item.label ?? "";
			}
		}
		return out;
	}, [results]);

	const isLoading = results.some((r) => r.isLoading);

	const getFieldLabel = useMemo(() => (key: string) => map[key] ?? "", [map]);

	return { getFieldLabel, isLoading };
}
